import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthTokens, UserSummary } from "../shared/types";
import { apiFetch, clearTokens, getAccessToken, logoutSession, restoreAccessToken, storeTokens } from "./api";

interface AuthValue {
  user: UserSummary | null;
  loading: boolean;
  login(email: string, password: string, mfaCode?: string): Promise<void>;
  register(input: {
    companyName: string;
    ownerName: string;
    email: string;
    password: string;
    legalRevision: string;
    turnstileToken?: string;
    invitationToken?: string;
    acceptEarlyAccess?: true;
  }): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        if (!getAccessToken()) {
          const restored = await restoreAccessToken();
          if (!restored) return;
        }
        const profile = await apiFetch<UserSummary>("/me");
        if (getAccessToken()) setUser(profile);
      } catch {
        clearTokens();
      } finally {
        setLoading(false);
      }
    };
    void bootstrap();
  }, []);

  const value = useMemo<AuthValue>(() => ({
    user,
    loading,
    async login(email: string, password: string, mfaCode?: string) {
      const tokens = await apiFetch<AuthTokens>("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Cochegest-Client": "browser" },
        body: JSON.stringify({ email, password, mfaCode: mfaCode || undefined }),
      });
      storeTokens(tokens);
      setUser(tokens.user);
    },
    async register(input) {
      const tokens = await apiFetch<AuthTokens>("/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Cochegest-Client": "browser" },
        body: JSON.stringify({ ...input, acceptTerms: true, acceptPrivacy: true }),
      });
      storeTokens(tokens);
      setUser(tokens.user);
    },
    async logout() {
      await logoutSession();
      setUser(null);
    },
  }), [loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return context;
}
